import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import { queryKeys, useNetworkKinds, useNetworkPage, usePager } from '../api/queries';
import type { NetworkListItem } from '../api/types';
import { driftPresentation } from '../components/presentation';
import { ActionButton, ErrorMessage, Loading, PageHeader, Pager, StatusBadge } from '../components/ui';
import { useCardTable } from '../lib/cardTable';
import { formatDateTime, formatRelative } from '../lib/format';
import { withParam } from '../lib/listParams';
import { strings } from '../strings';
import { attachmentWords } from './lists/helpers';
import { RegionChip } from './lists/parts';
import { RequestCountsCell } from './requests/RequestCountsCell';
import { NewNetworkDialog } from './NewNetworkDialog';
import { NetworkIcon, networkStatusPresentation, useManageNetworks } from './shared';

const s = strings.networks;
const l = s.lists.list;

function FilterField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const id = useId();
  return (
    <div className="search-field">
      <label htmlFor={id} className="sr-only">
        {l.filter}
      </label>
      <input
        id={id}
        type="search"
        className="input"
        placeholder={l.filter}
        value={value}
        data-list-search
        aria-keyshortcuts="/"
        onChange={(e) => onChange(e.target.value)}
      />
      <kbd aria-hidden className="kbd">
        /
      </kbd>
    </div>
  );
}

function RunCell({ network }: { network: NetworkListItem }) {
  const to = `/networks/${network.id}?tab=runs`;
  if (network.activeRun)
    return (
      <Link className="link" to={to}>
        {l.running(strings.status.runKind[network.activeRun.kind])}
      </Link>
    );
  if (network.queuedRuns > 0)
    return (
      <Link className="link" to={to}>
        <span className="badge tone-info">{l.queued(network.queuedRuns)}</span>
      </Link>
    );
  if (network.status === 'new' && !network.lastSuccessfulApply)
    return <span className="text-fg-muted">{l.nextPlan}</span>;
  return null;
}

function NetworksTable({ items }: { items: NetworkListItem[] }) {
  const cardTable = useCardTable();
  const kinds = useNetworkKinds();
  const topologyLabel = (id: string) => kinds.data?.topologies.find((t) => t.id === id)?.label ?? id;
  return (
    <div className="overflow-x-auto rounded-lg border border-line bg-surface">
      <table ref={cardTable} className="table table-cards net-list-table">
        <thead>
          <tr>
            <th scope="col">{l.columns.network}</th>
            <th scope="col">{l.columns.status}</th>
            <th scope="col">{l.columns.drift}</th>
            <th scope="col">{l.columns.topology}</th>
            <th scope="col">{l.columns.providers}</th>
            <th scope="col">{l.columns.attachments}</th>
            <th scope="col">{l.columns.requests}</th>
            <th scope="col">{l.columns.entryPoints}</th>
            <th scope="col">{l.columns.lastApply}</th>
            <th scope="col">{l.columns.run}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((network) => {
            const counts = network.attachments;
            const words = attachmentWords(counts);
            return (
              <tr key={network.id}>
                <td>
                  <span className="flex items-center gap-2">
                    <NetworkIcon aria-hidden className="size-4 shrink-0 text-fg-muted" />
                    <Link className="link font-medium" to={`/networks/${network.id}`}>
                      {network.name}
                    </Link>
                  </span>
                </td>
                <td>
                  <StatusBadge presentation={networkStatusPresentation[network.status]} />
                </td>
                <td>
                  {!network.lastSuccessfulApply
                    ? strings.common.dash
                    : network.drift !== 'none' && (
                        <StatusBadge presentation={driftPresentation[network.drift]} />
                      )}
                </td>
                <td>{topologyLabel(network.topology)}</td>
                <td>
                  <span className="flex flex-wrap gap-1">
                    {network.providers.flatMap((p) =>
                      p.regions.map((region) => (
                        <RegionChip key={`${p.provider}/${region}`} provider={p.provider} region={region} />
                      )),
                    )}
                  </span>
                </td>
                <td>
                  <span className="flex flex-col items-start gap-1">
                    {words && <span>{words}</span>}
                    {counts.waitingForApply > 0 && (
                      <span className="badge tone-warning">{l.waiting(counts.waitingForApply)}</span>
                    )}
                    {counts.failed > 0 && (
                      <span className="badge tone-danger">{l.failed(counts.failed)}</span>
                    )}
                    {counts.retained > 0 && (
                      <span className="badge tone-neutral">{l.retained(counts.retained)}</span>
                    )}
                  </span>
                </td>
                <td>
                  <RequestCountsCell counts={network.requests} />
                </td>
                <td className="tabular-nums">{network.entryPoints}</td>
                <td title={formatDateTime(network.lastSuccessfulApply?.completedAt)}>
                  {network.lastSuccessfulApply
                    ? formatRelative(network.lastSuccessfulApply.completedAt)
                    : l.never}
                </td>
                <td>
                  <RunCell network={network} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** `/networks` (spec 11 6.15.2, mock n01): the networks list; `?new=1` opens New network (6.15.3). */
export function NetworksPage() {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const manage = useManageNetworks();
  const pager = usePager();
  const page = useNetworkPage(pager.cursor);
  const [filter, setFilter] = useState('');
  const creating = search.get('new') === '1' && manage;
  const setCreating = (open: boolean) =>
    setSearch(withParam(search, 'new', open ? '1' : null), { replace: true });

  const items = useMemo(() => {
    const text = filter.trim().toLocaleLowerCase();
    return (page.data?.items ?? []).filter((n) => !text || n.name.toLocaleLowerCase().includes(text));
  }, [page.data, filter]);

  const newButton = (
    <ActionButton
      permission="read"
      disabledReason={manage ? null : s.requiresNetworkAdmin}
      variant="primary"
      icon={<Plus aria-hidden className="size-4" />}
      onClick={() => setCreating(true)}
    >
      {l.newNetwork}
    </ActionButton>
  );

  const body = () => {
    if (page.error) return <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />;
    if (!page.data) return <Loading />;
    if (page.data.items.length === 0 && !pager.hasPrevious)
      return (
        <div className="card p-4">
          <p>{l.empty}</p>
          <div className="mt-3">{manage ? newButton : <p className="help">{l.askAdmin}</p>}</div>
        </div>
      );
    return (
      <>
        {items.length === 0 ? <p className="help">{l.noMatch}</p> : <NetworksTable items={items} />}
        <Pager
          hasPrevious={pager.hasPrevious}
          nextCursor={page.data.nextCursor}
          onPrevious={pager.previous}
          onNext={pager.next}
        />
      </>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title={l.title}
        actions={
          <>
            <FilterField value={filter} onChange={setFilter} />
            {newButton}
          </>
        }
      >
        <p className="mt-0.5 text-fg-muted">{l.line}</p>
      </PageHeader>
      {body()}
      {creating && (
        <NewNetworkDialog
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            void queryClient.invalidateQueries({ queryKey: ['networks'] });
            queryClient.setQueryData(queryKeys.network(created.data.id), created);
            navigate(`/networks/${created.data.id}`);
          }}
        />
      )}
    </PageContainer>
  );
}
